import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { AccHub, AccProjektEintrag, AccVerbindung, ApiClient, ProfilEintrag } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { AccDurchsuchen } from './AccDurchsuchen';
import { AccZuordnung } from './AccZuordnung';
import { AccVersionen } from './AccVersionen';
import { RevitServerHinzufuegen } from '../revitserver/RevitServerHinzufuegen';
import { zeitpunkt } from './acczeit';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, ErrorState, Feld, LoadingState, StatusBadge, type Zustand,
} from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * „ACC VERBINDEN“ (AC1, Auftrag des Eigentümers F39 vom 26.10.2026: „ACC zuerst“)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * AC1a: die Verbindung der Plattform zu Autodesk. Ein SystemAdmin stimmt einmal mit dem Autodesk-Konto zu
 * (OAuth mit PKCE); danach erneuert die Plattform selbst. Widerruf und Fehler stehen hier für jeden sichtbar.
 * Die Token sieht die Oberfläche nie - der Server nennt nur Zustand, Konto und Zeiten.
 *
 * Die Agenten öffnen die Modelle weiter über den Cloud-Pfad mit ihrem eigenen, in Revit angemeldeten Konto; das
 * steht unten als Prüfliste.
 */

/** Zustandsmarke der Verbindung. */
export function verbindungsmarke(v: Pick<AccVerbindung, 'eingerichtet' | 'status'>): { zustand: Zustand; text: string } {
  if (!v.eingerichtet) return { zustand: 'neutral', text: 'nicht eingerichtet' };
  switch (v.status) {
    case 'verbunden': return { zustand: 'gut', text: 'verbunden' };
    case 'widerrufen': return { zustand: 'fehler', text: 'Zustimmung widerrufen' };
    case 'fehler': return { zustand: 'fehler', text: 'gestört' };
    default: return { zustand: 'neutral', text: 'nicht verbunden' };
  }
}

const GRUENDE: Record<string, string> = {
  abgelehnt: 'Die Zustimmung wurde bei Autodesk abgelehnt.',
  abgelaufen: 'Der Anmeldevorgang ist abgelaufen (10 Minuten). Bitte erneut verbinden.',
  bindung: 'Der Rücksprung kam nicht aus dem Browser, in dem die Verbindung begonnen wurde. '
    + 'Bitte im selben Browser erneut verbinden.',
  verbraucht: 'Dieser Rücksprung wurde schon verwendet. Bitte erneut verbinden.',
  zustand_unbekannt: 'Der Rücksprung gehört zu keinem Anmeldevorgang. Bitte erneut verbinden.',
  zustand_fehlt: 'Der Rücksprung gehört zu keinem Anmeldevorgang. Bitte erneut verbinden.',
  code_fehlt: 'Autodesk hat keinen Code geliefert. Bitte erneut verbinden.',
  tausch: 'Autodesk hat den Code nicht angenommen. Callback-URL und App-Typ der APS-App prüfen.',
  kein_refresh_token: 'Autodesk hat kein Refresh-Token ausgegeben. Die APS-App muss die 3-legged Anmeldung erlauben.',
  nicht_eingerichtet: 'Die ACC-Anbindung ist auf dem Server nicht eingerichtet.',
  schluessel: 'Der Anmeldevorgang passt nicht zum Token-Schlüssel des Servers. Bitte erneut verbinden.',
  autodesk: 'Autodesk hat die Anmeldung mit einem Fehler beendet. Bitte erneut verbinden.',
};

/** Klartext zur Rückmeldung nach dem Rücksprung (`?acc=…&grund=…`); `null` ohne Rückmeldung. */
export function rueckmeldungText(acc: string | null, grund: string | null): { zustand: Zustand; text: string } | null {
  if (acc === 'verbunden') return { zustand: 'gut', text: 'Verbunden. Autodesk hat die Zustimmung bestätigt.' };
  if (acc === 'fehler') {
    return { zustand: 'fehler', text: GRUENDE[grund ?? ''] ?? `Die Verbindung ist gescheitert (${grund ?? 'unbekannt'}).` };
  }
  return null;
}

/** Die Seite im Projektrahmen (`/projekte/:id/acc`). */
export function AccVerbindenSeite() {
  const { api, projekt, profile } = useOutletContext<Rahmen>();
  const [suche] = useSearchParams();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={4} spalten={2} />;
  return <AccVerbinden api={api} projektId={projekt.id} acc={suche.get('acc')} grund={suche.get('grund')} profile={profile} />;
}

export function AccVerbinden({
  api, projektId, acc = null, grund = null, weiterleiten = (url: string) => window.location.assign(url), profile = [],
}: {
  api: ApiClient;
  projektId: string;
  /** Profile des Projekts (AC1d: Profilfassung für Aufnahmen). */
  profile?: ProfilEintrag[];
  acc?: string | null;
  grund?: string | null;
  /** Der Sprung zu Autodesk - in Tests ersetzt. */
  weiterleiten?: (url: string) => void;
}) {
  const rueckmeldung = rueckmeldungText(acc, grund);
  const [gewaehlt, setGewaehlt] = useState<{ hub: AccHub; projekt: AccProjektEintrag } | null>(null);
  return (
    <>
      <PageHeader
        titel="ACC verbinden"
        beschreibung={'Die Verbindung der Plattform zu Autodesk Construction Cloud: einmal zustimmen, '
          + 'danach erneuert die Plattform selbst. Nur lesend - TGAPlan ändert in ACC nichts.'}
      />
      {rueckmeldung && (
        <div className="acc-rueckmeldung" role="status" data-rueckmeldung>
          <StatusBadge zustand={rueckmeldung.zustand} text={rueckmeldung.text} />
        </div>
      )}
      <Verbindungskarte api={api} projektId={projektId} weiterleiten={weiterleiten} />
      {/* AC1c: Zuordnung - Vorschlag mit Begründung, Bestätigung mit Grund. */}
      <AccZuordnung api={api} projektId={projektId} gewaehlt={gewaehlt} />
      {/* AC1d: neue Versionen - Hinweis, Aufnahme auf Klick, automatisch nur mit Einstellung. */}
      <AccVersionen api={api} projektId={projektId} profile={profile} />
      {/* AC1b: nur lesend durchblättern - lädt erst auf Klick; die Projektwahl geht an die Zuordnung. */}
      <AccDurchsuchen api={api} beiProjektwahl={(hub, projekt) => setGewaehlt({ hub, projekt })} />
      {/* RS1b (F436): Modell vom Revit Server hinzufügen - erscheint nur mit Schalter RevitServer:Aktiv und Recht. */}
      <RevitServerHinzufuegen api={api} projektId={projektId} />
      <ContentSection
        titel="Agenten-Konto"
        beschreibung={'Die Verarbeitungsmaschinen öffnen die Modelle weiter über den Cloud-Pfad mit dem Konto, '
          + 'das in Revit angemeldet ist - nicht mit der Verbindung oben.'}
      >
        <ul className="acc-pruefliste" data-pruefliste>
          <li>Das Agenten-Konto ist in jedem ACC-Projekt, das TGAPlan aufnimmt, Mitglied mit Zugriff auf „Docs“.</li>
          <li>Ordnerrecht mindestens „Anzeigen + Herunterladen“ auf den Modellordnern und auf den Ordnern der
            verlinkten Modelle (sie werden beim Öffnen nachgeladen).</li>
          <li>Für Rückschreib-Aufträge (heute nicht freigegeben) „Bearbeiten“ am Modellordner.</li>
          <li>Empfohlen: eine Rolle „TGAPlan-Agent“ mit diesen Rechten auf „Project Files“ - ein neues Projekt
            braucht dann nur die Mitgliedschaft.</li>
        </ul>
      </ContentSection>
    </>
  );
}

function Verbindungskarte({ api, projektId, weiterleiten }: {
  api: ApiClient; projektId: string; weiterleiten: (url: string) => void;
}) {
  const [stand, setStand] = useState<AccVerbindung | null>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [aktion, setAktion] = useState<'verbinden' | 'erneuern' | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [trennen, setTrennen] = useState(false);

  const laden = useCallback(() => {
    setLadefehler(null);
    return api.accVerbindung().then(setStand).catch(setLadefehler);
  }, [api]);

  useEffect(() => { laden(); }, [laden]);

  if (ladefehler) {
    return (
      <ContentSection titel="Verbindung">
        <ErrorState meldung="Der Stand der ACC-Verbindung konnte nicht geladen werden." technisch={ladefehler}
                    beiErneutVersuchen={() => { laden(); }} />
      </ContentSection>
    );
  }
  if (!stand) return <ContentSection titel="Verbindung"><LoadingState zeilen={4} spalten={2} /></ContentSection>;

  const marke = verbindungsmarke(stand);
  const verbunden = stand.eingerichtet && stand.status === 'verbunden';

  const verbinden = async () => {
    setAktion('verbinden');
    setAktionsfehler(null);
    try {
      const { url } = await api.accVerbindungStarten(projektId);
      weiterleiten(url);
    } catch (e) {
      setAktionsfehler(e instanceof Error ? e.message : String(e));
      setAktion(null);
    }
  };

  const erneuern = async () => {
    setAktion('erneuern');
    setAktionsfehler(null);
    setMeldung(null);
    try {
      const r = await api.accVerbindungErneuern();
      setStand(r.stand);
      setMeldung(r.erneuert ? 'Erneuert.' : 'Die Erneuerung ist gescheitert - siehe Fehler.');
    } catch (e) {
      setAktionsfehler(e instanceof Error ? e.message : String(e));
    } finally {
      setAktion(null);
    }
  };

  const zeilen: [string, string][] = [
    ['Autodesk-Konto', stand.konto ?? (verbunden ? 'unbekannt' : '')],
    ['Verbunden am', [zeitpunkt(stand.verbundenAm), stand.verbundenVon].filter(Boolean).join(' von ')],
    ['Zuletzt erneuert', zeitpunkt(stand.erneuertAm)],
    ['Zugriff gültig bis', zeitpunkt(stand.zugriffBis)],
    ['Zustimmung gilt ohne Erneuerung bis', zeitpunkt(stand.zustimmungGiltBis)],
    ['Bereiche', stand.bereiche ?? ''],
  ].filter(([, w]) => w !== '') as [string, string][];

  return (
    <ContentSection
      titel="Verbindung"
      className="acc-verbindung"
      meta={<StatusBadge zustand={marke.zustand} text={marke.text} />}
      aktionen={stand.rechte.verbinden && stand.eingerichtet ? (
        <>
          <button className="m-knopf m-knopf-haupt" onClick={verbinden} disabled={aktion !== null}>
            {stand.status === 'getrennt' ? 'Mit Autodesk verbinden' : 'Neu verbinden'}
          </button>{' '}
          {verbunden && (
            <><button className="m-knopf" onClick={erneuern} disabled={aktion !== null}>Jetzt erneuern</button>{' '}</>
          )}
          {stand.status !== 'getrennt' && (
            <button className="m-knopf" onClick={() => setTrennen(true)} disabled={aktion !== null}>Trennen …</button>
          )}
        </>
      ) : undefined}
    >
      {!stand.eingerichtet && (
        <p data-einrichtung>
          Die ACC-Anbindung ist auf dem Server nicht eingerichtet: {stand.einrichtungsFehler}
        </p>
      )}
      {stand.fehler && (
        <p data-fehler>
          <StatusBadge zustand={stand.status === 'verbunden' ? 'warnung' : 'fehler'} text="Letzter Fehler" />{' '}
          {stand.fehler}{stand.fehlerAm ? ` (${zeitpunkt(stand.fehlerAm)})` : ''}
        </p>
      )}
      {zeilen.length > 0 && (
        <dl className="acc-stand" data-stand>
          {zeilen.map(([k, w]) => (
            <div key={k}><dt>{k}</dt><dd>{w}</dd></div>
          ))}
        </dl>
      )}
      {stand.rueckrufadresse && (
        <p className="sehr-schwach" data-rueckruf>
          Callback-URL der APS-App: <code>{stand.rueckrufadresse}</code>
        </p>
      )}
      {!stand.rechte.verbinden && (
        <p className="sehr-schwach" data-rechte>Verbinden, Erneuern und Trennen darf nur ein SystemAdmin.</p>
      )}
      {meldung && <p role="status">{meldung}</p>}
      {aktionsfehler && <ErrorState klein meldung={aktionsfehler} />}
      <TrennenDialog
        offen={trennen}
        api={api}
        beiAbbrechen={() => setTrennen(false)}
        beiFertig={(neu) => { setTrennen(false); setStand(neu); setMeldung('Getrennt.'); }}
      />
    </ContentSection>
  );
}

function TrennenDialog({ offen, api, beiAbbrechen, beiFertig }: {
  offen: boolean; api: ApiClient; beiAbbrechen: () => void; beiFertig: (stand: AccVerbindung) => void;
}) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => { setGrund(''); setFehler(null); setLaeuft(false); }, [offen]);

  return (
    <ConfirmDialog
      offen={offen}
      art="warnung"
      titel="ACC-Verbindung trennen?"
      text={'Die Zustimmung wird bei Autodesk widerrufen und die Token werden gelöscht. Danach ruft TGAPlan '
        + 'Autodesk nicht mehr auf, bis ein SystemAdmin neu verbindet.'}
      bestaetigenText="Trennen"
      laeuft={laeuft}
      fehler={fehler}
      beiAbbrechen={beiAbbrechen}
      beiBestaetigen={async () => {
        if (grund.trim().length < 3) { setFehler(new Error('Bitte einen Grund angeben (mindestens 3 Zeichen).')); return; }
        setLaeuft(true);
        setFehler(null);
        try {
          beiFertig(await api.accVerbindungTrennen(grund.trim()));
        } catch (e) {
          setFehler(e);
          setLaeuft(false);
        }
      }}
    >
      <Feld art="textfeld" beschriftung="Grund" wert={grund} beiAenderung={(w) => setGrund(w as string)}
            zeilen={2} pflicht breite="voll" platzhalter="z. B. Kontowechsel" />
    </ConfirmDialog>
  );
}
